import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import type { User, LoginCredentials, AuthResponse } from '@/types/auth'

// Credenciais de demo para funcionamento sem API
const DEMO_CREDENTIALS = {
  username: 'admin',
  password: 'IwFfFBeZOrQBHbk7g9FNw'
}

const DEMO_USER: User = {
  id: '1',
  username: 'admin',
  email: 'admin@spacenetwork.psi.br',
  role: 'admin',
  twoFactorEnabled: false,
  createdAt: new Date().toISOString(),
  lastLogin: new Date().toISOString()
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const demoMode = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  function initAuth() {
    const savedToken = localStorage.getItem('xdns_token')
    const savedUser = localStorage.getItem('xdns_user')
    
    if (savedToken && savedUser) {
      token.value = savedToken
      user.value = JSON.parse(savedUser)
      demoMode.value = savedToken === 'demo_token'
      if (!demoMode.value) {
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`
      }
    }
  }

  async function login(credentials: LoginCredentials): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      // Tentar API real primeiro
      const response = await api.post<AuthResponse>('/api/v1/auth/login', credentials)
      
      token.value = response.data.token
      user.value = response.data.user
      demoMode.value = false
      
      localStorage.setItem('xdns_token', response.data.token)
      localStorage.setItem('xdns_user', JSON.stringify(response.data.user))
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
      
      return true
    } catch (err: any) {
      // Se API falhar, tentar modo demo
      if (credentials.username === DEMO_CREDENTIALS.username && 
          credentials.password === DEMO_CREDENTIALS.password) {
        token.value = 'demo_token'
        user.value = DEMO_USER
        demoMode.value = true
        
        localStorage.setItem('xdns_token', 'demo_token')
        localStorage.setItem('xdns_user', JSON.stringify(DEMO_USER))
        
        return true
      }
      
      error.value = err.response?.data?.message || 'Credenciais inválidas'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      if (!demoMode.value) {
        await api.post('/api/v1/auth/logout')
      }
    } catch {
      // Ignore logout errors
    } finally {
      token.value = null
      user.value = null
      demoMode.value = false
      localStorage.removeItem('xdns_token')
      localStorage.removeItem('xdns_user')
      delete api.defaults.headers.common['Authorization']
    }
  }

  async function refreshToken() {
    if (demoMode.value) {
      return true // Demo mode always valid
    }
    try {
      const response = await api.post<AuthResponse>('/api/v1/auth/refresh')
      token.value = response.data.token
      localStorage.setItem('xdns_token', response.data.token)
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
      return true
    } catch {
      await logout()
      return false
    }
  }

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    demoMode,
    initAuth,
    login,
    logout,
    refreshToken,
  }
})
